import type {
  CurriculumNodeView,
  LcTestSummaryView,
  TestSelection,
} from '@/types/coaching'

export interface PendingOfflineTestTarget {
  selection: TestSelection
  prerequisiteLessonId: number
  hasExistingTest: boolean
}

export interface ActiveCumulativeTestTarget {
  selection: TestSelection
  /** 누적 테스트 바로 앞 레슨 — 트리 기본 펼침 기준 */
  predecessorLessonId: number | null
}

/**
 * 활성화(ACTIVE)됐지만 아직 끝나지 않은 누적 테스트. 학습자 오늘 수업은 이 테스트를 다음 레슨보다 먼저 연다.
 */
export function findActiveCumulativeTest(tree: CurriculumNodeView[]): ActiveCumulativeTestTarget | null {
  for (const curriculum of tree) {
    for (const course of curriculum.courses) {
      for (const module of course.modules) {
        for (const test of module.tests ?? []) {
          if (test.kind !== 'CUMULATIVE_ASSESSMENT') continue
          if (test.cumulativeAssessmentStatus !== 'ACTIVE' || test.cumulativeAssessmentId == null) continue
          const predecessorIndex = Math.min(test.afterLessonOrdinal, module.lessons.length) - 1
          return {
            selection: {
              kind: test.kind,
              chapterOrdinal: test.chapterOrdinal,
              scopeContentId: test.scopeContentId,
              chapterTitle: module.title,
              cumulativeAssessmentId: test.cumulativeAssessmentId,
            },
            predecessorLessonId: module.lessons[predecessorIndex]?.lessonId ?? null,
          }
        }
      }
    }
  }
  return null
}

function testKey(kind: string, chapterOrdinal: number, scopeContentId: number): string {
  return `${kind}-${chapterOrdinal}-${scopeContentId}`
}

/**
 * Finds the first unlocked chapter/cumulative test that has not been completed yet.
 * An undefined summary list means test state is still unknown; an empty list means
 * the unlocked test has not been generated yet.
 */
export function findPendingOfflineTest(
  tree: CurriculumNodeView[],
  summaries: LcTestSummaryView[] | undefined,
): PendingOfflineTestTarget | null {
  if (summaries == null) return null

  const latestSummaryByKey = new Map<string, LcTestSummaryView>()
  for (const summary of summaries) {
    if (summary.kind !== 'CHAPTER') continue
    const key = testKey(summary.kind, summary.chapterOrdinal, summary.scopeContentId)
    const current = latestSummaryByKey.get(key)
    if (current == null || summary.roundNo > current.roundNo) {
      latestSummaryByKey.set(key, summary)
    }
  }

  for (const curriculum of tree) {
    for (const course of curriculum.courses) {
      for (const module of course.modules) {
        for (const test of module.tests ?? []) {
          if (test.kind !== 'CHAPTER') continue

          const prerequisiteIndex = Math.min(test.afterLessonOrdinal, module.lessons.length) - 1
          const prerequisite = module.lessons[prerequisiteIndex]
          if (prerequisite?.status !== 'completed') continue

          const summary = latestSummaryByKey.get(
            testKey(test.kind, test.chapterOrdinal, test.scopeContentId),
          )
          if (summary?.status === 'COMPLETED') continue

          return {
            selection: {
              kind: test.kind,
              chapterOrdinal: test.chapterOrdinal,
              scopeContentId: test.scopeContentId,
              chapterTitle: module.title,
            },
            prerequisiteLessonId: prerequisite.lessonId,
            hasExistingTest: summary != null,
          }
        }
      }
    }
  }

  return null
}
